import { DEFAULT_POOL_SIZE, MIN_DAYS_OUT, addDays, daysBetween, startOfDay } from '@/utils/studyPlan';

const SAVE_DELAY_MS = 600;
let saveTimer = null;

// YYYY-MM-DD in local time
function toDateString(date) {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromDateString(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const defaultState = () => ({
  loaded: false,
  introSeen: false,
  firstName: null,
  daysOut: 47,
  examDateBooked: true,
  studyDays: [true, true, true, true, true, false, false],
  minutes: 60,
  catchUp: 'weekly',
  startedAt: null,
  justStarted: false,
  editing: false,
  // The lesson opened from the plan, so finishing it returns there
  returnToPlanLessonId: null,
  // The quiz (by category) opened from the plan, so its results page links back there
  returnToPlanQuizCategoryId: null,
  // Score of that lesson, shown in the encouragement message on return
  lessonResult: null,
  poolSize: DEFAULT_POOL_SIZE,
  // Weakest topics, then untried ones: [{ id, name, accuracy, answered }]
  suggestedTopics: [],
});

const state = defaultState;

const getters = {
  loaded: state => state.loaded,
  introSeen: state => state.introSeen,
  firstName: (state, getters, rootState, rootGetters) => state.firstName ?? rootGetters['user/get']?.firstName ?? null,
  inputs: state => ({
    daysOut: state.daysOut,
    examDateBooked: state.examDateBooked,
    studyDays: state.studyDays,
    minutes: state.minutes,
    catchUp: state.catchUp,
  }),
  // Has a plan and isn't editing it in the builder
  started: state => !!state.startedAt && !state.editing,
  startedAt: state => state.startedAt,
  // 'created' or 'updated' right after the student saves from the builder
  justStarted: state => state.justStarted,
  poolSize: state => state.poolSize,
  suggestedTopics: state => state.suggestedTopics,
  returnToPlanLessonId: state => state.returnToPlanLessonId,
  returnToPlanQuizCategoryId: state => state.returnToPlanQuizCategoryId,
  lessonResult: state => state.lessonResult,
  enabled: (state, getters, rootState, rootGetters) => rootGetters['user/featureEnabled']('featureStudyPlanner'),
  payload: state => ({
    examDate: toDateString(addDays(startOfDay(new Date()), state.daysOut)),
    examDateEstimated: !state.examDateBooked,
    studyWeekdays: state.studyDays.map((on, i) => (on ? i : -1)).filter(i => i >= 0),
    minutesPerDay: state.minutes,
    catchUp: state.catchUp,
  }),
};

const mutations = {
  hydrate(state, { studyPlan, poolSize, suggestedTopics }) {
    state.loaded = true;
    if (poolSize) state.poolSize = poolSize;
    state.suggestedTopics = suggestedTopics || [];
    if (!studyPlan) return;

    const daysOut = daysBetween(new Date(), fromDateString(studyPlan.examDate));
    state.daysOut = Math.max(MIN_DAYS_OUT, daysOut);
    state.examDateBooked = !studyPlan.examDateEstimated;
    state.studyDays = Array.from({ length: 7 }, (_, i) => studyPlan.studyWeekdays.includes(i));
    state.minutes = studyPlan.minutesPerDay;
    state.catchUp = studyPlan.catchUp;
    state.startedAt = studyPlan.startedAt;
    state.introSeen = !!studyPlan.introSeenAt;
  },
  // Back to a brand new plan, as after a progress reset
  reset(state) {
    Object.assign(state, defaultState());
  },
  setIntroSeen(state, seen = true) {
    state.introSeen = seen;
  },
  setFirstName(state, name) {
    state.firstName = name;
  },
  setDaysOut(state, { daysOut, booked = true }) {
    state.daysOut = daysOut;
    state.examDateBooked = booked;
  },
  toggleStudyDay(state, index) {
    // Keep at least one study day
    if (state.studyDays[index] && state.studyDays.filter(Boolean).length === 1) return;
    state.studyDays = state.studyDays.map((on, i) => (i === index ? !on : on));
  },
  setMinutes(state, minutes) {
    state.minutes = minutes;
  },
  setReturnToPlan(state, lessonId) {
    state.returnToPlanLessonId = lessonId;
  },
  setReturnToPlanQuiz(state, categoryId) {
    state.returnToPlanQuizCategoryId = categoryId;
  },
  setLessonResult(state, result) {
    state.lessonResult = result;
  },
  setCatchUp(state, catchUp) {
    state.catchUp = catchUp;
  },
  start(state) {
    state.justStarted = state.startedAt ? 'updated' : 'created';
    state.startedAt = state.startedAt || Date.now();
    state.editing = false;
  },
  setJustStarted(state, value) {
    state.justStarted = value;
  },
  edit(state) {
    state.editing = true;
  },
};

const actions = {
  load({ commit, rootGetters }) {
    return rootGetters['session/ajax'].get('/study_plan').then(({ data }) => {
      commit('hydrate', data);
      return data;
    });
  },
  save({ getters, rootGetters }, extra = {}) {
    clearTimeout(saveTimer);
    return rootGetters['session/ajax'].patch('/study_plan', { ...getters.payload, ...extra });
  },
  // Saves once the builder has been still for a moment
  queueSave({ dispatch }) {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => dispatch('save'), SAVE_DELAY_MS);
  },
  finishIntro({ commit, dispatch }, name) {
    commit('setIntroSeen');
    if (name) {
      commit('setFirstName', name);
      dispatch('user/update', { user: { firstName: name } }, { root: true });
    }
    return dispatch('save', { introSeen: true });
  },
  // Builds a quiz on the given topics, or the student's weak topics, and returns its category
  async createWeakTopicQuiz({ commit, dispatch, rootGetters }, { categoryIds } = {}) {
    const { data } = await rootGetters['session/ajax'].post('/study_plan/quiz', categoryIds ? { categoryIds } : undefined);
    const categories = data.records || data.categories;
    commit('categories/setCategories', categories, { root: true });
    await dispatch('session/loadUser', null, { root: true });
    return categories[0];
  },
  start({ commit, dispatch }) {
    return dispatch('save', { started: true }).then(() => commit('start'));
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
